import React from "react";
import {Link, Link as RouterLink} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Delete, Edit} from "@material-ui/icons";
import styled from "styled-components";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import {CustomCheckbox, formatDatetime, modal} from "../../../../util";
import {useLoading} from "../../../../util/LoadingProvider";
import {toast} from "react-toastify";
import OrganizationPositionPagesForm from "./organizationPositionPagesForm";
import PaginationBar from "../../../common/PaginationBar";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragHandleIcon from "@material-ui/icons/DragHandle";

const useStyles = makeStyles((theme) => ({
    textInput: {
        backgroundColor: "white",
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    inactive: {
        color: "#999999",
    },
    buttonPosition: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    button: {
        backgroundColor: "#306F9D",
        textTransform: "capitalize",
        color: "white",
        "&:hover": {
            backgroundColor: "#306F9D",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

class DraggableTableCell extends React.Component {
    getSnapshotBeforeUpdate(prevProps) {
        if (!this.ref) {
            return null;
        }

        const isDragStarting =
            this.props.isDragOccurring && !prevProps.isDragOccurring;

        if (!isDragStarting) {
            return null;
        }

        const { width, height } = this.ref.getBoundingClientRect();
        return { width, height };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const ref = this.ref;
        if (!ref) return;

        if (snapshot) {
            if (ref.style.width === snapshot.width) return;
            ref.style.width = `${snapshot.width}px`;
            ref.style.height = `${snapshot.height}px`;
            return;
        }

        if (this.props.isDragOccurring) return;

        if (ref.style.width == null) return;

        ref.style.removeProperty("height");
        ref.style.removeProperty("width");
    }

    setRef = (ref) => {
        this.ref = ref;
    };

    render() {
        return (
            <TableCell
                style={{ paddingTop: 12, paddingBottom: 12 }}
                ref={this.setRef}
                align={this.props.align}
            >
                {this.props.children}
            </TableCell>
        );
    }
}

const DraggableTableRow = ({
    snapshot,
    row,
    provided,
    orderingDisabled,
    isDragging,
}) => {
    return (
        <TableRow
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{
                ...provided.draggableProps.style,
                background: snapshot.isDragging ? "#f5f5f5" : "white",
            }}
        >
            {row.cells.map((cell, index) => {
                return index !== row.cells.length - 1 && (
                    <DraggableTableCell key={index} isDragOccurring={isDragging}>
                        {cell.render("Cell")}
                    </DraggableTableCell>
                );
            })}

            <TableCell
                style={{ paddingBottom: 12, color: "#CCCCCC", cursor: "grab" }}
                {...(!orderingDisabled && { ...provided.dragHandleProps })}
            >
                <DragHandleIcon />
            </TableCell>
        </TableRow>
    );
};

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

function OrganizationPositionPages(props) {
    const {match, disabled} = props;
    const classes = useStyles();
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const {setLoading} = useLoading();
    const [totalElements, setTotalElements] = React.useState(0);
    const [queryString, setQueryString] = React.useState("");
    const [isDragging, setIsDragging] = React.useState(false);
    const [orderingDisabled, setOrderingDisabled] = React.useState(false);

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const deletePosition = async (id) => {
        const decision = await modal.fire({
            title: "Delete Position",
            text: "Are you sure to delete this Position?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deletePositionById(id);
                if (result.status === 200) {
                    toast.dark("Position deleted.");
                    await getPositions();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const columns = React.useMemo(
      () => [
        {
          Header: "Name (English)",
          accessor: "positionNameEn",
          Cell: ({ row }) => {
            return (
              <Link
                style={{
                  textDecoration: "none",
                  color: "#306F9D",
                }}
                to={`${match.url}/${row.original.id}`}
              >
                {row.original.positionNameEn}
              </Link>
            );
          },
        },
        {
          Header: "Name (Traditional Chinese)",
          accessor: "positionNameTc",
          Cell: ({ row }) => {
            return (
              <Link
                style={{
                  textDecoration: "none",
                  color: "#306F9D",
                }}
                to={`${match.url}/${row.original.id}`}
              >
                {row.original.positionNameTc}
              </Link>
            );
          },
        },
        {
            Header: "Group",
            accessor: "parentGroupName",
            Cell: ({ row }) => {
                return (
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "#306F9D",
                        }}
                      to={`/hkiff/organization/group/${row.original.parentGroupId}`}
                    >
                        {row.original.parentGroupName}
                    </Link>
                );
            },
        },
        {
            Header: "Last Update",
            accessor: "updateTime",
            Cell: ({row}) => {
                return row.original.updateTime ? formatDatetime(row.original.updateTime) : '-';
            },
        },
      ],
      []
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    Header: () => <div>Action</div>,
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex"}}>
                            <IconButton
                                className={classes.iconBtn}
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            {disabled || (
                                <IconButton
                                    className={classes.iconBtn}
                                    size="small"
                                    onClick={() => {
                                        deletePosition(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit"/>
                                </IconButton>
                            )}
                        </div>
                    ),
                },
                {
                    id: "dragHandle",
                    Header: () => <></>,
                },
            ]);
        }
    );

    const getPositions = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getPositions({
                page: pageIndex,
                pageSize,
                keyword: queryString,
            });
            console.log('RESULT:', result)
            setData(result.data.content);
            setTotalElements(result.data.totalElements);
            setTotalPages(result.data.totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [setLoading, pageIndex, pageSize, queryString]);

    React.useEffect(() => {
        getPositions();
    }, [getPositions]);

    const triggerSearch = React.useCallback(() => {
        setQueryString(keyword);
        setOrderingDisabled(keyword ? true : false);
    }, [gotoPage, keyword, setLoading]);

    const onBeforeDragStart = () => {
        setIsDragging(true);
    };

    const onDragEnd = (result) => {
        setIsDragging(false);

        if (!result.destination) {
            return;
        }

        const items = reorder(data, result.source.index, result.destination.index);
        setData(items);
    };

    const saveOrdering = async () => {
        try {
            const newArray = data.map((item, index) => ({
                id: item.id,
                sequence: index + pageSize * pageIndex,
            }));

            const result = await apiHelper.hkiff.updatePositionSeq(newArray);
            
            if (result.data.code === 0) {
                toast.dark("Your changes are saved.");
                await getPositions();
            } else {
                toast.error("Failed to save changes.");
            }
        } catch (err) {
            console.error("Save ordering error:", err);
            toast.error("An error occurred while saving.");
        }
    };

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
              Organization &gt; Position
            </Typography>
            <Grid container spacing={4}>
            <Grid item xs={4}>
            <OrganizationPositionPagesForm
            description="Add New Position"
            submitBtnTxt="Add New Position"
            gridSize={4}
            upperBtnPositionSize="75%"
            fetchData={getPositions}
            disabled={disabled}
          />
            </Grid>
              <Grid item xs={8}>
              <form>
                  <Grid
                      container
                      justify="space-between"
                      style={{paddingBottom: "10px"}}
                  >
                      <Grid container item xs={6} md={6}>
                          <Button
                              variant="contained"
                              color="primary"
                              onClick={saveOrdering}
                              disabled={orderingDisabled || disabled}
                          >
                              Save Changes
                          </Button>
                      </Grid>
                      <Grid container item xs={6} md={6}>
                          <Box
                              width="100%"
                              component="span"
                              display="flex"
                              justifyContent="flex-end"
                              alignItems="center"
                          >
                              <Box display="flex">
                                  <TextField
                                      id="outlined-error"
                                      label="Search"
                                      variant="outlined"
                                      margin="dense"
                                      value={keyword}
                                      onChange={(e) => setKeyword(e.target.value)}
                                  />
                              </Box>
                              <Box display="flex">
                                  <Button
                                      variant="outlined"
                                      color="primary"
                                      type="button"
                                      margin="dense"
                                      onClick={triggerSearch}
                                  >
                                      Search
                                  </Button>
                              </Box>
                          </Box>
                      </Grid>
                  </Grid>
                  <Grid container xs={12}>
                      <TableContainer
                          component={Paper}
                          style={{
                              boxShadow: "none",
                              border: "1px solid #cccccc",
                          }}
                      >
                          <fieldset disabled={disabled}
                                    className="fieldset">
                              <IsDraggingContext.Provider value={isDragging}>
                                  <Table
                                      aria-label="simple table"
                                      component="ul"
                                      style={{margin: "0"}}
                                      {...getTableProps()}
                                  >
                                      <colgroup>
                                          <col
                                              style={{width: "5%"}}/>
                                          <col
                                              style={{width: "15"}}/>
                                          <col
                                              style={{width: "15"}}/>
                                        <col
                                              style={{width: "15"}}/>
                                          <col
                                              style={{width: "15"}}/>
                                          <col
                                              style={{width: "5%"}}/>
                                          <col
                                              style={{width: "5%"}}/>
                                      </colgroup>
                                      <TableHead component="div">
                                          {headerGroups.map((headerGroup) => (
                                              <TableRow {...headerGroup.getHeaderGroupProps()}>
                                                  {headerGroup.headers.map((column) => (
                                                      <TableCell
                                                          {...column.getHeaderProps()}
                                                          className={`${column.className ?? ""}`}
                                                          style={{
                                                              paddingTop: 6,
                                                              paddingBottom: 6,
                                                              lineHeight: "1.1em",
                                                              height: "48px",
                                                          }}
                                                      >
                                                          {column.render("Header")}
                                                      </TableCell>
                                                      ))}
                                              </TableRow>
                                          ))}
                                      </TableHead>
                                      <DragDropContext
                                          onBeforeDragStart={onBeforeDragStart}
                                          onDragEnd={onDragEnd}
                                      >
                                          <Droppable droppableId="table-droppable">
                                              {(provided, snapshot) => (
                                                  <TableBody
                                                      component="div"
                                                      ref={provided.innerRef}
                                                      {...provided.droppableProps}
                                                  >
                                                      {rows.map((row, index) => {
                                                          prepareRow(row);
                                                          return (
                                                              <Draggable
                                                                  key={row.original.id}
                                                                  draggableId={String(row.original.id)}
                                                                  index={index}
                                                                  isDragDisabled={disabled || orderingDisabled}
                                                              >
                                                                  {(provided, snapshot) => (
                                                                      <DraggableTableRow
                                                                          row={row}
                                                                          provided={provided}
                                                                          snapshot={snapshot}
                                                                          orderingDisabled={orderingDisabled}
                                                                          isDragging={isDragging}
                                                                      />
                                                                  )}
                                                              </Draggable>
                                                          );
                                                      })}
                                                      {provided.placeholder}
                                                  </TableBody>
                                              )}
                                          </Droppable>
                                      </DragDropContext>
                                  </Table>
                              </IsDraggingContext.Provider>
                          </fieldset>
                      </TableContainer>
                  </Grid>
                  <Grid
                      container
                      justify="flex-end"
                      style={{
                          paddingRight: "25px",
                          marginTop: "15px"
                      }}
                  >
                      <div className={classes.flexRow}>
                          <div></div>
                          <PaginationBar
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            gotoPage={gotoPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageIndex={pageIndex}
                            totalCount={totalElements}
                          />
                      </div>
                  </Grid>
              </form>
              </Grid>
            </Grid>
        </div>
    );
}

export default OrganizationPositionPages;